import { StockDto } from '../../../types/stocks';
import { investmentTypeToLabel } from '../../../utils/investmentType';

// Core holding analysis structure
export interface HoldingAnalysis {
  symbol: string;
  investmentType: string;
  shares: number;
  avgPrice: number;
  currentPrice: number;
  totalInvested: number;
  currentValue: number;
  unrealizedGain: number;
  unrealizedGainPct: number;
  dividends: number;
  totalReturn: number; // includes dividends
  totalReturnPct: number;
  weight: number; // to be filled after computing all holdings
  firstPurchaseDate?: string;
}

export interface PortfolioAggregate {
  totalInvested: number;
  totalValue: number;
  unrealizedGain: number;
  unrealizedGainPct: number;
  totalDividends: number;
  totalReturn: number;
  totalReturnPct: number;
  yieldOnCost: number; // dividends / invested
  riskConcentrationIndex: number; // Herfindahl-Hirschman of weights
  topContributors: { symbol: string; contributionValue: number; contributionPct: number }[];
}

// (moved) type mapping handled by investmentTypeToLabel util

export function computeHoldings(stocks: StockDto[]): HoldingAnalysis[] {
  const holdings: HoldingAnalysis[] = stocks.map(s => {
    // Fallback cambio: prefer currentCambio; if missing for non-EUR, average purchase cambio
    const currency = (s as any).currency;
    let stockCambio: number = typeof (s as any).currentCambio === 'number' ? Number((s as any).currentCambio) : 1;
    if (currency && currency !== 'EUR' && (!stockCambio || stockCambio === 0)) {
      const cambios: number[] = s.purchases
        .map((p: any) => (typeof p.cambio === 'number' ? Number(p.cambio) : (typeof p.pricePerShareEUR === 'number' && p.pricePerShare ? Number(p.pricePerShareEUR) / Number(p.pricePerShare) : NaN)))
        .filter((v: number) => Number.isFinite(v) && v > 0) as number[];
      if (cambios.length > 0) {
        stockCambio = cambios.reduce((a: number, b: number) => a + b, 0) / cambios.length;
      }
    }

    // Sales mapping to compute remaining quantity per purchase
    const soldByPurchase = new Map<string, number>();
    const sales = Array.isArray((s as any).sales) ? (s as any).sales : [];
    sales.forEach((sale: any) => {
      const spList = Array.isArray(sale.salePurchases) ? sale.salePurchases : [];
      spList.forEach((sp: any) => {
        const pid = sp.purchasePublicId;
        const qty = Number(sp.quantitySold || 0);
        if (pid) soldByPurchase.set(pid, (soldByPurchase.get(pid) || 0) + qty);
      });
    });

    // Remaining shares and EUR invested
    let shares = 0;
    let totalInvestedEUR = 0;
    s.purchases.forEach((p: any) => {
      const totalQty = Number(p.quantity || 0);
      const soldQty = Math.min(totalQty, Number(soldByPurchase.get(p.publicId) || 0));
      const remQty = Math.max(0, totalQty - soldQty);
      shares += remQty;
      const perShareEUR = typeof p.pricePerShareEUR === 'number'
        ? Number(p.pricePerShareEUR)
        : Number(p.pricePerShare || 0) * (typeof p.cambio === 'number' ? Number(p.cambio) : (currency === 'EUR' ? 1 : stockCambio));
      totalInvestedEUR += remQty * perShareEUR;
    });

    if (shares <= 0) return null as any; // filter later

    // Current price in EUR
    const currentPriceEUR = Number((s.currentPrice || 0)) * (currency === 'EUR' ? 1 : (stockCambio && stockCambio > 0 ? stockCambio : 1));
    const currentValueEUR = shares * currentPriceEUR;

    const avgPriceEUR = shares > 0 ? totalInvestedEUR / shares : 0;
    const unrealizedGainEUR = currentValueEUR - totalInvestedEUR;
    const unrealizedGainPct = totalInvestedEUR > 0 ? (unrealizedGainEUR / totalInvestedEUR) * 100 : 0;
    const dividendsEUR = (s.dividends || []).reduce((sum, d) => sum + (d.amount || 0), 0);
    const totalReturnEUR = (currentValueEUR + dividendsEUR) - totalInvestedEUR;
    const totalReturnPct = totalInvestedEUR > 0 ? (totalReturnEUR / totalInvestedEUR) * 100 : 0;
    const firstPurchaseDate = s.purchases.length ? s.purchases.reduce((earliest, p) => earliest < p.date ? earliest : p.date, s.purchases[0].date) : undefined;
    return {
      symbol: s.symbol,
      investmentType: investmentTypeToLabel((s as any).investmentType as any, s.symbol),
      shares,
      avgPrice: avgPriceEUR,
      currentPrice: currentPriceEUR,
      totalInvested: totalInvestedEUR,
      currentValue: currentValueEUR,
      unrealizedGain: unrealizedGainEUR,
      unrealizedGainPct,
      dividends: dividendsEUR,
      totalReturn: totalReturnEUR,
      totalReturnPct,
      weight: 0,
      firstPurchaseDate
    } as HoldingAnalysis;
  }).filter(Boolean);

  const totalValue = holdings.reduce((sum, h) => sum + h.currentValue, 0);
  holdings.forEach(h => { h.weight = totalValue > 0 ? (h.currentValue / totalValue) * 100 : 0; });
  return holdings.sort((a,b) => b.currentValue - a.currentValue);
}

export function aggregatePortfolio(holdings: HoldingAnalysis[]): PortfolioAggregate {
  const totalInvested = holdings.reduce((s,h) => s + h.totalInvested, 0);
  const totalValue = holdings.reduce((s,h) => s + h.currentValue, 0);
  const unrealizedGain = totalValue - totalInvested;
  const unrealizedGainPct = totalInvested > 0 ? (unrealizedGain / totalInvested) * 100 : 0;
  const totalDividends = holdings.reduce((s,h) => s + h.dividends, 0);
  const totalReturn = (totalValue + totalDividends) - totalInvested;
  const totalReturnPct = totalInvested > 0 ? (totalReturn / totalInvested) * 100 : 0;
  const yieldOnCost = totalInvested > 0 ? (totalDividends / totalInvested) * 100 : 0;
  const riskConcentrationIndex = holdings.reduce((sum,h) => sum + Math.pow(h.weight/100, 2), 0); // 0..1

  const contributions = holdings.map(h => ({
    symbol: h.symbol,
    contributionValue: h.unrealizedGain,
    contributionPct: totalInvested > 0 ? (h.unrealizedGain / totalInvested) * 100 : 0
  })).sort((a,b) => Math.abs(b.contributionValue) - Math.abs(a.contributionValue)).slice(0,5);

  return {
    totalInvested,
    totalValue,
    unrealizedGain,
    unrealizedGainPct,
    totalDividends,
    totalReturn,
    totalReturnPct,
    yieldOnCost,
    riskConcentrationIndex,
    topContributors: contributions
  };
}

export function groupBySector(holdings: HoldingAnalysis[]): { sector: string; value: number; stocks: string[]; percentage: number }[] {
  // Placeholder sectors (could integrate external classification later)
  const sectorMap: Record<string,string> = {
    'AAPL':'Tecnologia','MSFT':'Tecnologia','GOOGL':'Tecnologia','AMZN':'Consumo discricionário','TSLA':'Consumo discricionário',
    'JPM':'Serviços financeiros','JNJ':'Saúde','PFE':'Saúde','XOM':'Energia','SPY':'ETF diversificado','QQQ':'ETF de tecnologia','VTI':'ETF diversificado'
  };
  const acc = new Map<string,{ value:number; stocks:string[] }>();
  holdings.forEach(h => {
    const sector = sectorMap[h.symbol.toUpperCase()] || 'Outros';
    if(!acc.has(sector)) acc.set(sector,{ value:0, stocks:[] });
    const bucket = acc.get(sector)!; bucket.value += h.currentValue; bucket.stocks.push(h.symbol);
  });
  const totalValue = holdings.reduce((s,h)=>s+h.currentValue,0);
  return Array.from(acc.entries()).map(([sector,data])=>({
    sector,
    value: data.value,
    stocks: data.stocks,
    percentage: totalValue>0 ? (data.value/totalValue)*100 : 0
  })).sort((a,b)=>b.value-a.value);
}

export function allocationByType(holdings: HoldingAnalysis[]): { name:string; value:number; percentage:number }[] {
  const map = new Map<string,number>();
  holdings.forEach(h => map.set(h.investmentType, (map.get(h.investmentType)||0)+h.currentValue));
  const totalValue = holdings.reduce((s,h)=>s+h.currentValue,0);
  return Array.from(map.entries()).map(([name,value])=>({
    name,
    value,
    percentage: totalValue>0 ? (value/totalValue)*100 : 0
  }));
}

// Simple CSV exporter for quick win button (caller triggers download)
export function holdingsToCsv(holdings: HoldingAnalysis[], aggregate: PortfolioAggregate): string {
  // Portuguese spreadsheet style: ';' separator and decimal comma
  const dec = (value: number, digits: number): string => value.toFixed(digits).replace('.', ',');
  const header = 'Símbolo;Tipo;Unidades;Preço médio;Preço atual;Investido;Valor atual;Ganho não realizado;Ganho não realizado %;Dividendos;Rendimento total %;Peso %';
  const rows = holdings.map(h => [
    h.symbol,
    h.investmentType,
    String(h.shares).replace('.', ','),
    dec(h.avgPrice, 4),
    dec(h.currentPrice, 4),
    dec(h.totalInvested, 2),
    dec(h.currentValue, 2),
    dec(h.unrealizedGain, 2),
    dec(h.unrealizedGainPct, 2),
    dec(h.dividends, 2),
    dec(h.totalReturnPct, 2),
    dec(h.weight, 2)
  ].join(';'));
  const footer = [`TOTAL;;;; ;${dec(aggregate.totalInvested, 2)};${dec(aggregate.totalValue, 2)};${dec(aggregate.unrealizedGain, 2)};${dec(aggregate.unrealizedGainPct, 2)};${dec(aggregate.totalDividends, 2)};${dec(aggregate.totalReturnPct, 2)},`];
  return [header, ...rows, ...footer].join('\n');
}
